import React, { useState, useEffect, useMemo } from "react";
import { Button } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../misc/Loading';
import Turbo from '../misc/Turbo';
import { getModuleStatus, requestStart } from '../../store/ModuleStatusAction';
import { subscribe } from '../../store/NotificationAction';
import StatusCode from '../../util/StatusCode';
import { useParams, Link } from 'react-router-dom';
import InstancePhase from "./components/instancePhase/InstancePhase";
import { useTranslation, Trans } from 'react-i18next';
import { CoreModuleStatus } from "../../models/CoreModels";
import { EmptyRequestState, RequestState } from "../../models/RequestState";
import * as Notifications from '../../services/Notifications'
import { RootState } from "../../store/store";
import ModuleSyncStatus from "../misc/ModuleSyncStatus";
import { FlowSyncAccessPolicy, isAuthorized } from '../../models/FlowSyncAccessPolicy';


const ModuleStatus = () => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const [lastMessageId, setMessageId] = useState<string | undefined>(undefined);

    const startRequest = useSelector<RootState, EmptyRequestState>(state => state.moduleStatus.startRequest);
    const notification = useSelector<RootState, Partial<Notifications.NotificationServiceMessage>>(state => state.notification);
    const moduleStatus = useSelector<RootState, RequestState<CoreModuleStatus>>(state => state.moduleStatus.moduleStatus);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);

    const { t } = useTranslation(['configuration', 'common']);

    useEffect(() => {
        if (moduleId) {
            getModuleStatus(moduleId, true)(dispatch);
            return subscribe(['moduleInstanceStartRequest', 'moduleInstanceStart', 'moduleInstancePhaseUpdate', 'moduleInstanceComplete'])(dispatch);
        }
    }, [dispatch, moduleId]);

    useEffect(() => {
        if (moduleStatus.code === StatusCode.COMPLETE && (lastMessageId !== notification.messageId)) {
            setMessageId(notification.messageId);
            if (moduleId) {
                getModuleStatus(moduleId, false)(dispatch);
            }
        }
    }, [moduleStatus.code, notification, dispatch, lastMessageId, moduleId]);

    const onClickRequestStart = async () => {
        if (moduleId) {
            await requestStart(moduleId)(dispatch);
        }
    }

    const renderRequestStartStatus = () => {
        switch (startRequest.code) {
            case StatusCode.PENDING: return <Loading style={{ display: 'inline' }} />;
            case StatusCode.ERROR: return t('error.somethingWentWrong', { ns: 'common' });
            case StatusCode.COMPLETE: return t('moduleStatus.success');
            default: return null;
        }
    }

    const renderStatusMessage = (status: CoreModuleStatus) => {
        if (status.runningTrailingModule) {
            return (
                <div className="push--vertical flex">
                    <FontAwesomeIcon icon="info-circle" className="icon_active blue push--right" title={t('status.warning', { ns: 'common' })} />
                    <span>
                        <Trans t={t}
                            i18nKey="moduleStatus.runningTrailingModuleWarning"
                            values={{ trailingModule: status.runningTrailingModule.title }}
                            components={{ a: <Link to={`/module/${status.runningTrailingModule.id}/status`} /> }} />
                    </span>
                </div>)
        }
    }

    const startModuleAccess = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.START_MODULE);
    }, [authorizedAccessPolicies])

    const renderBody = () => {
        switch (moduleStatus.code) {
            case StatusCode.NONE: return <Loading />;
            case StatusCode.PENDING: return <Loading />;
            case StatusCode.ERROR: return t('moduleStatus.noRunInfo');
            case StatusCode.COMPLETE:

                const quarantined = moduleStatus.data.isInQuarantine;
                const active = moduleStatus.data.isActive;
                const canRequestStart = startRequest.code !== StatusCode.PENDING && active && !quarantined && startModuleAccess;
                let disabledTooltip = undefined;
                if (!startModuleAccess) {
                disabledTooltip = t('moduleStatus.disabled.missingPermission');
                } else if (quarantined) {
                disabledTooltip = t('moduleStatus.disabled.quarantined');
                } else if (!active) {
                disabledTooltip = t('moduleStatus.disabled.inactive');
                }

                return <>
                    <div>
                        <h5>{t('moduleStatus.heading')}</h5>
                        <div style={{ margin: '1rem 0' }}>
                            <ModuleSyncStatus
                                status={moduleStatus.data}
                                displayQuarantine={true}
                                runnable={moduleStatus.data.runnable}
                            />
                            {renderStatusMessage(moduleStatus.data)}
                        </div>
                        {moduleStatus.data.runnable && 
                        <span title={disabledTooltip}>
                        <Button color="primary" className="start-request" disabled={!canRequestStart} onClick={() => onClickRequestStart()}>{t('moduleStatus.start')}</Button>
                        </span>
                        }
                        {renderRequestStartStatus()}
                    </div>
                    <Turbo />
                    {moduleId && moduleStatus.data.runnable &&
                        <InstancePhase
                            moduleInstanceId={moduleStatus.data.id}
                            moduleId={moduleId}
                            completed={Boolean(moduleStatus.data?.completedAt)}
                        />
                    }
                </>;
            default: return (<p>
                <Trans
                    t={t}
                    i18nKey="moduleStatus.error"
                    components={{
                        a: <Link to="/module" />
                    }} />
            </p>)
        }
    }

    return (
        <div className="mt-4">
            {renderBody()}
        </div>
    );
}

export default ModuleStatus;
